import React, { memo, useMemo } from 'react';
import { Tabs, Text } from '@Sage/hoi-poi-ui';
import { useNavigate, useParams, Route, Routes, Navigate } from 'react-router-dom';

import DetailLayout from 'containers/components/layouts/ContentDetailLayout';
import { getLiteral } from 'utils/getLiteral';

import ContentNavigator from 'containers/campaigns/marketing/ContentNavigator';
import SenderSettingsHeader from './senders/SenderSettingsHeader';
import DomainSettingsHeader from './domains/DomainSettingsHeader';
import SendersSettings from './senders';
import DomainsSettings from './domains';
import UnsubscribeSettings from './unsubscribe';

import './styles.scss';
import DomainVerificationBanner from '../components/DomainVerificationBanner';

const TABS = {
    senders: 'senders',
    unsubscribe: 'unsubscribe',
    domains: 'domains',
};

const MarketingSettings = memo(() => {
    const { '*': slug } = useParams();
    const navigate = useNavigate();

    const tabs = useMemo(
        () => [
            {
                key: TABS.senders,
                title: getLiteral('label_marketing_campaigns_senders'),
            },
            {
                key: TABS.unsubscribe,
                title: getLiteral('label_marketing_campaigns_unsubscribe_process'),
            },
            {
                key: TABS.domains,
                title: getLiteral('label_marketing_campaigns_domain_verification'),
            },
        ],
        [],
    );

    const activeTab = useMemo(() => {
        const matchedTab = Object.values(TABS).find((tab) => slug?.includes(tab));
        return matchedTab || TABS.senders;
    }, [slug]);

    const handleTabChange = (key) => {
        navigate(`/campaigns/marketing/settings/${key}`);
    };

    const header = useMemo(() => {
        const emptyHeader = <div className="marketing-settings__header" />;
        const headerMap = {
            [TABS.senders]: () => <SenderSettingsHeader />,
            [TABS.domains]: () => <DomainSettingsHeader />,
        };

        return headerMap[activeTab]?.() || emptyHeader;
    }, [activeTab]);

    return (
        <DetailLayout
            header={header}
            postHeader={<DomainVerificationBanner />}
            navigator={<ContentNavigator />}
        >
            <div className="marketing-settings__main">
                <div className="marketing-settings__main__header">
                    <Text type="h5" bold>
                        {getLiteral('label_settings')}
                    </Text>
                </div>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    tabs={tabs}
                    className="marketing-settings__tabs"
                />
                <Routes>
                    <Route path={`/${TABS.senders}`} element={<SendersSettings />} />
                    <Route path={`/${TABS.domains}`} element={<DomainsSettings />} />
                    <Route path={`/${TABS.unsubscribe}`} element={<UnsubscribeSettings />} />
                    <Route path="*" element={<Navigate to={TABS.senders} replace />} />
                </Routes>
            </div>
        </DetailLayout>
    );
});

MarketingSettings.displayName = 'MarketingSettings';

export default MarketingSettings;
